// cookieManager.js - Comprehensive cookie management utility

class CookieManager {
    constructor() {
        this.consentGiven = this.getConsentStatus();
        this.sessionId = this.getOrCreateSessionId();
        this.userId = this.getOrCreateUserId();
    }

    // === CORE COOKIE OPERATIONS ===
    
    /**
     * Set a cookie with proper configuration
     * @param {string} name - Cookie name
     * @param {string} value - Cookie value
     * @param {Object} options - Cookie options
     */
    setCookie(name, value, options = {}) {
        const defaults = {
            expires: 30, // days
            path: '/',
            secure: window.location.protocol === 'https:',
            sameSite: 'Lax'
        };
        
        const config = { ...defaults, ...options };
        
        let cookieString = `${name}=${encodeURIComponent(value)}`;
        
        if (config.expires) {
            const date = new Date();
            date.setTime(date.getTime() + (config.expires * 24 * 60 * 60 * 1000));
            cookieString += `; expires=${date.toUTCString()}`;
        }
        
        cookieString += `; path=${config.path}`;
        cookieString += `; SameSite=${config.sameSite}`;
        
        if (config.secure) {
            cookieString += '; Secure';
        }
        
        document.cookie = cookieString;
    }

    /**
     * Get cookie value by name
     * @param {string} name - Cookie name
     * @returns {string|null} Cookie value or null if not found
     */
    getCookie(name) {
        const nameEQ = name + "=";
        const ca = document.cookie.split(';');
        
        for (let i = 0; i < ca.length; i++) {
            let c = ca[i];
            while (c.charAt(0) === ' ') c = c.substring(1, c.length);
            if (c.indexOf(nameEQ) === 0) {
                return decodeURIComponent(c.substring(nameEQ.length, c.length));
            }
        }
        return null;
    }

    /**
     * Delete a cookie
     * @param {string} name - Cookie name
     */
    deleteCookie(name) {
        this.setCookie(name, '', { expires: -1 });
    }

    // === CONSENT MANAGEMENT ===
    
    /**
     * Check if user has given consent for analytics cookies
     * @returns {boolean} Consent status
     */
    getConsentStatus() {
        const consent = this.getCookie('fp_consent');
        return consent === 'granted';
    }

    /**
     * Set user consent for analytics cookies
     * @param {boolean} granted - Whether consent is granted
     */
    setConsent(granted) {
        this.consentGiven = granted;
        this.setCookie('fp_consent', granted ? 'granted' : 'denied', { expires: 365 });
        
        if (!granted) {
            this.clearAnalyticsCookies();
        }
    }

    /**
     * Clear all analytics cookies when consent is withdrawn
     */
    clearAnalyticsCookies() {
        const analyticsCookies = [
            'fp_user_id', 'fp_session_data', 'fp_preferences',
            'fp_decision_history', 'fp_interaction_data'
        ];
        
        analyticsCookies.forEach(cookie => this.deleteCookie(cookie));
    }

    // === SESSION MANAGEMENT ===
    
    /**
     * Get or create a unique session ID
     * @returns {string} Session ID
     */
    getOrCreateSessionId() {
        let sessionId = this.getCookie('fp_session_id');
        
        if (!sessionId) {
            sessionId = this.generateUniqueId();
            this.setCookie('fp_session_id', sessionId, { expires: 1 }); // 1 day session
        }
        
        return sessionId;
    }

    /**
     * Get or create a unique user ID (anonymous)
     * @returns {string} User ID
     */
    getOrCreateUserId() {
        if (!this.consentGiven) return null;
        
        let userId = this.getCookie('fp_user_id');
        
        if (!userId) {
            userId = this.generateUniqueId();
            this.setCookie('fp_user_id', userId, { expires: 365 }); // 1 year
        }
        
        return userId;
    }

    /**
     * Generate a unique identifier
     * @returns {string} Unique ID
     */
    generateUniqueId() {
        return 'fp_' + Date.now().toString(36) + Math.random().toString(36).substr(2);
    }

    // === APPLICATION STATE PERSISTENCE ===
    
    /**
     * Save decision progress to prevent data loss
     * @param {Object} decisionData - Current decision state
     */
    saveDecisionProgress(decisionData) {
        const progressData = {
            timestamp: Date.now(),
            dilemma: decisionData.dilemma,
            pathNames: decisionData.pathNames,
            criteria: decisionData.criteria,
            pathValues: decisionData.pathValues,
            sessionId: this.sessionId
        };
        
        this.setCookie('fp_decision_progress', JSON.stringify(progressData), { expires: 7 });
    }

    /**
     * Load saved decision progress
     * @returns {Object|null} Saved decision data or null
     */
    loadDecisionProgress() {
        const progressCookie = this.getCookie('fp_decision_progress');
        
        if (progressCookie) {
            try {
                const data = JSON.parse(progressCookie);
                // Check if data is not too old (7 days)
                const isRecent = (Date.now() - data.timestamp) < (7 * 24 * 60 * 60 * 1000);
                return isRecent ? data : null;
            } catch (e) {
                console.warn('Failed to parse decision progress cookie:', e);
                return null;
            }
        }
        
        return null;
    }

    /**
     * Clear saved decision progress
     */
    clearDecisionProgress() {
        this.deleteCookie('fp_decision_progress');
    }

    // === USER PREFERENCES ===
    
    /**
     * Save user preferences
     * @param {Object} preferences - User preferences object
     */
    savePreferences(preferences) {
        if (!this.consentGiven) return;
        
        const prefData = {
            showTips: preferences.showTips,
            showExample: preferences.showExample,
            theme: preferences.theme || 'default',
            accessibility: preferences.accessibility || {},
            timestamp: Date.now()
        };
        
        this.setCookie('fp_preferences', JSON.stringify(prefData), { expires: 365 });
    }

    /**
     * Load user preferences
     * @returns {Object} User preferences
     */
    loadPreferences() {
        if (!this.consentGiven) return this.getDefaultPreferences();
        
        const prefCookie = this.getCookie('fp_preferences');
        
        if (prefCookie) {
            try {
                return JSON.parse(prefCookie);
            } catch (e) {
                console.warn('Failed to parse preferences cookie:', e);
            }
        }
        
        return this.getDefaultPreferences();
    }

    /**
     * Get default preferences
     * @returns {Object} Default preferences
     */
    getDefaultPreferences() {
        return {
            showTips: true,
            showExample: false,
            theme: 'default',
            accessibility: {}
        };
    }

    // === ANALYTICS DATA ===
    
    /**
     * Track user interaction
     * @param {string} action - Action name
     * @param {Object} data - Additional data
     */
    trackInteraction(action, data = {}) {
        if (!this.consentGiven) return;
        
        const interactionData = {
            action,
            data,
            timestamp: Date.now(),
            sessionId: this.sessionId,
            userId: this.userId,
            url: window.location.pathname
        };
        
        // Store in session storage for batch sending
        this.addToInteractionQueue(interactionData);
    }

    /**
     * Add interaction to queue for batch processing
     * @param {Object} interactionData - Interaction data
     */
    addToInteractionQueue(interactionData) {
        const queue = JSON.parse(sessionStorage.getItem('fp_interaction_queue') || '[]');
        queue.push(interactionData);
        
        // Limit queue size
        if (queue.length > 100) {
            queue.shift();
        }
        
        sessionStorage.setItem('fp_interaction_queue', JSON.stringify(queue));
    }

    /**
     * Get and clear interaction queue
     * @returns {Array} Interaction queue
     */
    getInteractionQueue() {
        const queue = JSON.parse(sessionStorage.getItem('fp_interaction_queue') || '[]');
        sessionStorage.removeItem('fp_interaction_queue');
        return queue;
    }

    // === DECISION ANALYTICS ===
    
    /**
     * Track decision completion
     * @param {Object} decisionData - Complete decision data
     */
    trackDecisionCompletion(decisionData) {
        if (!this.consentGiven) return;
        
        const completionData = {
            event: 'decision_completed',
            sessionId: this.sessionId,
            userId: this.userId,
            timestamp: Date.now(),
            decisionMetrics: {
                numPaths: Object.keys(decisionData.pathNames || {}).length,
                numCriteria: (decisionData.criteria || []).length,
                timeToComplete: this.getSessionDuration(),
                criteriaCategories: decisionData.criteriaCategorization || [],
                usedExample: decisionData.usedExample || false,
                usedTips: decisionData.usedTips || false
            }
        };
        
        this.trackInteraction('decision_completed', completionData);
    }

    /**
     * Get session duration in minutes
     * @returns {number} Session duration
     */
    getSessionDuration() {
        const sessionStart = this.getCookie('fp_session_start');
        if (sessionStart) {
            return Math.round((Date.now() - parseInt(sessionStart)) / 60000);
        }
        return 0;
    }

    /**
     * Mark session start
     */
    markSessionStart() {
        if (!this.getCookie('fp_session_start')) {
            this.setCookie('fp_session_start', Date.now().toString(), { expires: 1 });
        }
    }

    // === UTILITY METHODS ===
    
    /**
     * Get all Focal Path cookies for debugging
     * @returns {Object} All FP cookies
     */
    getAllFPCookies() {
        const cookies = {};
        const allCookies = document.cookie.split(';');
        
        allCookies.forEach(cookie => {
            const [name, value] = cookie.trim().split('=');
            if (name.startsWith('fp_')) {
                cookies[name] = decodeURIComponent(value || '');
            }
        });
        
        return cookies;
    }

    /**
     * Clear all Focal Path cookies
     */
    clearAllFPCookies() {
        const cookies = this.getAllFPCookies();
        Object.keys(cookies).forEach(cookieName => {
            this.deleteCookie(cookieName);
        });
    }
}

// Export singleton instance
export default new CookieManager();
